import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  FiShoppingCart, FiHeart, FiStar, FiEye,
  FiTrendingUp, FiAward, FiZap, FiPackage,
  FiChevronRight, FiCheck,
} from 'react-icons/fi';
import { MdLocalOffer, MdOutlineNewReleases } from 'react-icons/md';
import { GiChiliPepper, GiHerbsBundle, GiCoolSpices, GiWheat } from 'react-icons/gi';
import { MdLocalFlorist } from 'react-icons/md';
import { useCart } from '../context/CartContext';
import { useProducts } from '../context/ProductsContext';
import './FeaturedSpices.css';

/* ── icon map (same keys used in ProductsContext) ─── */
const iconMap = {
  cool:    <GiCoolSpices />,
  chili:   <GiChiliPepper />,
  herb:    <GiHerbsBundle />,
  wheat:   <GiWheat />,
  florist: <MdLocalFlorist />,
};

/* ── category → visual style ─── */
const catStyle = {
  'Whole Spices':    { accent: '#8B6914', bg: 'linear-gradient(135deg, #fdf8f0 0%, #f5e6cc 100%)' },
  'Powders':         { accent: '#e8472a', bg: 'linear-gradient(135deg, #fff5f0 0%, #ffe8e0 100%)' },
  'Blends & Masala': { accent: '#c94b0a', bg: 'linear-gradient(135deg, #fff8f0 0%, #ffecd8 100%)' },
  'Sauces':          { accent: '#df0c0c', bg: 'linear-gradient(135deg, #fff5f5 0%, #ffe0e0 100%)' },
  'Honey & Health':  { accent: '#d4780a', bg: 'linear-gradient(135deg, #fffbf0 0%, #ffefc0 100%)' },
};

/* ── badge → icon key ─── */
const badgeIconKey = (badge) => {
  if (!badge) return 'zap';
  const b = badge.toLowerCase();
  if (b.includes('best') || b.includes('popular') || b.includes('fan')) return 'trending';
  if (b.includes('new')) return 'new';
  if (b.includes('sale') || b.includes('value')) return 'offer';
  if (b.includes('premium') || b.includes('top') || b.includes('natural') || b.includes('superfood')) return 'award';
  return 'zap';
};

/* ── map context product → card-compatible shape ─── */
const toCard = (p) => {
  const style = catStyle[p.category] || catStyle['Powders'];
  return {
    ...p,
    accentColor: style.accent,
    bgGradient:  style.bg,
    badgeIcon:   badgeIconKey(p.badge),
    subtitle:    `${p.origin} · ${p.weight}`,
    reviewCount: p.reviews,
    inStock:     true,
  };
};

const filters = ['All', 'Best Sellers', 'New Arrivals', 'On Sale', 'Premium'];

/* ── sub-components ─── */
const BadgeIcon = ({ type }) => {
  const icons = {
    trending: <FiTrendingUp />,
    new:      <MdOutlineNewReleases />,
    offer:    <MdLocalOffer />,
    award:    <FiAward />,
  };
  return icons[type] || <FiZap />;
};

const StarRating = ({ rating, count }) => {
  const full = Math.floor(rating);
  const partial = rating % 1;
  return (
    <div className="star-rating">
      <div className="stars">
        {[...Array(5)].map((_, i) => (
          <span key={i} className="star" style={{
            color:   i < full ? '#fabc2a' : i === full && partial > 0 ? '#fabc2a' : '#e0e0e0',
            opacity: i === full && partial > 0 ? partial : 1,
          }}>
            <FiStar fill={i < full || (i === full && partial > 0) ? '#fabc2a' : 'none'} />
          </span>
        ))}
      </div>
      <span className="rating-value">{rating}</span>
      <span className="review-count">({count.toLocaleString()})</span>
    </div>
  );
};

const SpiceCard = ({ spice, index }) => {
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [addedToCart, setAddedToCart]   = useState(false);
  const cardRef = useRef(null);
  const { addToCart } = useCart();

  const handleAddToCart = () => {
    addToCart(spice);
    setAddedToCart(true);
    setTimeout(() => setAddedToCart(false), 2000);
  };

  const discount = spice.originalPrice
    ? Math.round((1 - spice.price / spice.originalPrice) * 100)
    : null;

  const handleMouseMove = (e) => {
    const card = cardRef.current;
    if (!card) return;
    const rect = card.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width - 0.5) * 12;
    const y = ((e.clientY - rect.top) / rect.height - 0.5) * -12;
    card.style.setProperty('--rx', `${y}deg`);
    card.style.setProperty('--ry', `${x}deg`);
  };

  const handleMouseLeave = () => {
    const card = cardRef.current;
    if (!card) return;
    card.style.setProperty('--rx', '0deg');
    card.style.setProperty('--ry', '0deg');
  };

  return (
    <article
      ref={cardRef}
      className={`spice-card ${spice.featured ? 'spice-card--featured' : ''}`}
      style={{ '--index': index, '--accent': spice.accentColor }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      {/* Visual area */}
      <div className="card-visual" style={{ background: spice.bgGradient }}>
        {spice.badge && (
          <div className={`card-badge card-badge--${spice.badgeIcon}`}>
            <BadgeIcon type={spice.badgeIcon} />
            <span>{spice.badge}</span>
          </div>
        )}

        <button
          className={`wishlist-btn ${isWishlisted ? 'active' : ''}`}
          onClick={() => setIsWishlisted(!isWishlisted)}
          aria-label={isWishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
        >
          <FiHeart fill={isWishlisted ? 'currentColor' : 'none'} />
        </button>

        {discount && <div className="discount-tag">−{discount}%</div>}

        <div className="card-emoji-wrap">
          <div className="card-emoji-ring" style={{ borderColor: spice.accentColor + '30' }}>
            {spice.imageSrc ? (
              <img
                src={spice.imageSrc}
                alt={spice.name}
                className="card-product-img"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
            ) : null}
            <span
              className="card-icon"
              style={{ color: spice.accentColor, display: spice.imageSrc ? 'none' : 'flex' }}
            >
              {iconMap[spice.iconType] || iconMap.cool}
            </span>
          </div>
          <div className="card-dots">
            {[...Array(6)].map((_, i) => (
              <span key={i} className="dot" style={{ background: spice.accentColor + '40' }} />
            ))}
          </div>
        </div>

        <div className="card-quickview">
          <button className="quickview-btn"><FiEye /><span>Quick View</span></button>
        </div>
      </div>

      {/* Body */}
      <div className="card-body">
        <div className="card-origin">
          <span className="origin-dot" style={{ background: spice.accentColor }} />
          {spice.origin}
        </div>

        <h3 className="card-name">{spice.name}</h3>
        <p className="card-subtitle">{spice.subtitle}</p>

        <StarRating rating={spice.rating} count={spice.reviewCount} />

        <p className="card-description">{spice.description}</p>

        <div className="card-footer">
          <div className="price-block">
            <span className="price-weight">{spice.weight}</span>
            <div className="price-row">
              <span className="price-current">TZS {Number(spice.price).toLocaleString()}</span>
              {spice.originalPrice && (
                <span className="price-original">TZS {Number(spice.originalPrice).toLocaleString()}</span>
              )}
            </div>
          </div>

          <button
            className={`add-to-cart-btn ${addedToCart ? 'added' : ''}`}
            onClick={handleAddToCart}
            aria-label={`Add ${spice.name} to cart`}
          >
            {addedToCart ? <><FiCheck /><span>Added!</span></> : <><FiShoppingCart /><span>Add</span></>}
          </button>
        </div>
      </div>
    </article>
  );
};

/* ── MAIN ─── */
const FeaturedSpices = () => {
  const { products } = useProducts();
  const [activeFilter, setActiveFilter] = useState('All');
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setIsVisible(true); observer.disconnect(); } },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const getFiltered = () => {
    let pool = [...products];
    if (activeFilter === 'Best Sellers')
      pool = pool.filter(p => p.badge === 'Best Seller' || p.badge === 'Top Rated' || p.badge === 'Popular');
    else if (activeFilter === 'New Arrivals')
      pool = pool.filter(p => p.badge === 'New');
    else if (activeFilter === 'On Sale')
      pool = pool.filter(p => p.originalPrice);
    else if (activeFilter === 'Premium')
      pool = pool.filter(p => p.price >= 8000);
    return pool
      .sort((a, b) => b.reviews - a.reviews)
      .slice(0, 3)
      .map(toCard);
  };

  const featured = getFiltered();

  return (
    <section
      id="spices"
      ref={sectionRef}
      className={`featured-spices ${isVisible ? 'in-view' : ''}`}
    >
      <div className="section-bg" aria-hidden="true">
        <div className="bg-blob bg-blob--1" />
        <div className="bg-blob bg-blob--2" />
        <div className="bg-pattern" />
      </div>

      <div className="fs-container">
        <header className="fs-header">
          <div className="header-eyebrow">
            <span className="eyebrow-line" />
            <span className="eyebrow-text">Handpicked for You</span>
            <span className="eyebrow-line" />
          </div>

          <h2 className="fs-title">
            <span className="title-line title-line--1">Featured</span>
            <span className="title-line title-line--2">
              Spices<span className="title-accent"> &amp; Blends</span>
            </span>
          </h2>

          <p className="fs-subtitle">
            Sourced directly from family farms and cooperatives across Tanzania.
            Every batch tested, certified, and delivered at peak freshness.
          </p>

          <div className="header-stats">
            {[
              { value: `${products.length}+`, label: 'Varieties' },
              { value: '5',                   label: 'Categories' },
              { value: '50k+',                label: 'Happy Cooks' },
            ].map(stat => (
              <div key={stat.label} className="stat-pill">
                <strong>{stat.value}</strong>
                <span>{stat.label}</span>
              </div>
            ))}
          </div>
        </header>

        <div className="filter-bar" role="tablist" aria-label="Filter spices">
          {filters.map(filter => (
            <button key={filter} role="tab"
              aria-selected={activeFilter === filter}
              className={`filter-btn ${activeFilter === filter ? 'active' : ''}`}
              onClick={() => setActiveFilter(filter)}
            >
              {filter}
            </button>
          ))}
        </div>

        <div className="spice-grid">
          {featured.length > 0 ? (
            featured.map((spice, i) => <SpiceCard key={spice.id} spice={spice} index={i} />)
          ) : (
            <div className="empty-state">
              <GiCoolSpices size={40} />
              <p>No spices found for this filter.</p>
            </div>
          )}
        </div>

        <div className="fs-footer">
          <div className="footer-trust">
            {[
              { icon: <FiPackage />, text: 'Same-day delivery in Dar es Salaam' },
              { icon: <FiAward />,   text: 'Quality guaranteed' },
              { icon: <FiZap />,     text: 'Ships within 24hrs' },
            ].map(item => (
              <div key={item.text} className="trust-item">
                {item.icon}<span>{item.text}</span>
              </div>
            ))}
          </div>

          <Link to="/shop" className="view-all-btn">
            <span>Explore All {products.length} Spices</span>
            <FiChevronRight />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default FeaturedSpices;
